import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useLazyQuery } from "@apollo/client";

import { Button, Card, FormGroup, PageHeader } from "@cofano/cck";
import { Box, CircularProgress, Grid2 as Grid, Tab, Tabs } from "@mui/material";
import Info from "@mui/icons-material/Info";

import useTabs from "components/hooks/useTabs";
import Helmet from "components/helmet/Helmet";
import Documents, { DocumentOptionalColumns } from "components/documents/Documents";

import {
	DataFormat,
	Fields,
	hasOptionalFieldPermission,
	hasPluginPermission,
	Language,
	usePermission,
} from "imports/Utils";

import { FleetLink } from "utils/LinkUtils";

import ShipGeneralDetails from "./components/details/ShipGeneralDetails";
import ShipHoldDetails from "./components/details/ShipHoldDetails";
import ShipTanksDetails from "./components/details/ShipTanksDetails";
import ShipContactDetails from "./components/details/ShipContactDetails";
import ShipAvailabilityDetails from "./components/details/ShipAvailabilityDetails";
import ShipTonnageCertificatesTable from "./components/table/ShipTonnageCertificatesTable";
import ShipAgentsTable from "./components/table/ShipAgentsTable";
import LastShippedProductsTable from "./components/table/LastShippedProductsTable";
import UpdateShipNameDialog from "./components/dialogs/UpdateShipNameDialog";

import {
	ShipTabs,
	adnCargoTankDesign,
	adnCargoTankType,
	adnShipType,
	hasAdnCargoTankDesignPermission,
	hasAdnCargoTankTypePermission,
	hasAdnShipTypePermission,
	hasBobbyCatPermission,
	hasBoilerPermission,
	hasCapacityTeuPermission,
	hasCellGuidesPermission,
	hasClassificationSocietyPermission,
	hasDeckLoadPermission,
	hasDroplinePermission,
	hasEniPermission,
	hasImoPermission,
	hasInsurerPermission,
	hasLicensePlatePermission,
	hasMaxCranesOnQuaysPermission,
	hasMemoPermission,
	hasNiwoPermission,
	hasNumberOfCranesPermission,
	hasNumberOfTanksPermission,
	hasPumpCapacityPermission,
	hasPushBowPermission,
	hasReeferConnectionPermission,
	hasSailersPermission,
	hasShuttersPermission,
	hasShuttersTypePermission,
	hasSpillRailPermission,
	hasTankCladdingTypePermission,
	hasTracksPermission,
	hasTrussingPermission,
} from "./utils/ShipUtils";

import { ChildProps } from "types/commons";
import { BargeType, DocumentEntity, DocumentType } from "__generated__/enums";
import { GetShipDocument, GetShipQuery } from "__generated__/view/ships/graphql/ship.queries.generated";

export type ShipFromQuery = ChildProps<GetShipQuery, "ship">;

const ShipDetails = () => {

	const { id } = useParams();

	const [ getShip, { data, refetch, loading } ] = useLazyQuery(GetShipDocument);

	useEffect(() => {
		if (id) {
			getShip({
				variables: {
					id: Number(id),
				},
			});
		}
	}, [ id ]);

	return (
		<React.Fragment>
			{data?.ship && !loading ? (
				<Page ship={data.ship} refetch={refetch} />
			) : (
				<CircularProgress size={30} />
			)}
		</React.Fragment>
	);
};

const Page = ({ ship, refetch }: { ship: ShipFromQuery; refetch: () => void; }) => {

	const session = useStoreState(store => store.session);

	const [ dialogOpen, openDialog ] = useState(false);

	const navigate = useNavigate();
	const location = useLocation();
	const { isRTR, isOperator, isMasterdata, isReadOnly, isSkipperOrShipManager } = usePermission();

	const { selectedTab, selectTab } = useTabs<ShipTabs>({
		defaultSelected: location.state?.tab || ShipTabs.General,
	});

	const canEdit = !ship.deprecated && (isRTR || isOperator || isMasterdata) && !isReadOnly;

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("SHIP")}
					<Helmet title={intl.formatMessage({ id: "SHIP" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{!isSkipperOrShipManager && (
						<Button onClick={() => navigate("/view/ships")} variant="outlined">
							{intl.formatMessage({ id: "SHIPS" })}
						</Button>
					)}

					{canEdit && isRTR && (
						<Button onClick={() => openDialog(true)} variant="outlined">
							{intl.formatMessage({ id: "EDIT_NAME" })}
						</Button>
					)}

					{canEdit && (
						<Button onClick={() => navigate(`/view/ships/${ship.id}/edit`, { state: { tab: selectedTab } })}>
							{intl.formatMessage({ id: "EDIT" })}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			<Grid container spacing={3}>
				<Grid size={{ xs: 12, md: 5 }}>
					<Card title={Language("DETAILS")} icon={<Info />}>
						<Details ship={ship} />
					</Card>
				</Grid>

				<Grid size={{ xs: 12, md: 7 }}>
					<Card disablePadding>
						<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
							<Tabs
								variant='scrollable'
								value={selectedTab}
								onChange={(_, newValue) => selectTab(newValue)}>
								<Tab label={Language("GENERAL")} value={ShipTabs.General} />

								{ship.bargeType !== BargeType.Tanker
									&& hasOptionalFieldPermission(session, Fields.SHIP, "holds")
									&& (
										<Tab label={Language("HOLDS")} value={ShipTabs.Holds} />
									)}

								{ship.bargeType === BargeType.Tanker
									&& hasOptionalFieldPermission(session, Fields.SHIP, "tanks")
									&& (
										<Tab label={Language("TANKS")} value={ShipTabs.Tanks} />
									)
								}

								{/*TODO: Reimplement in the future*/}
								{/*{hasOptionalFieldPermission(session, Fields.SHIP, "layerCapacities") && (*/}
								{/*	<Tab label={Language("SHIPS_LAYER_CAPACITIES")} value={TABS.LAYER_CAPACITIES} />*/}
								{/*)}*/}

								{hasOptionalFieldPermission(session, Fields.SHIP, "contacts") && (
									<Tab label={Language("CONTACTS")} value={ShipTabs.Contacts} />
								)}

								{hasPluginPermission(session, "shipAvailability") && (
									<Tab label={Language("SHIP_AVAILABILITY")} value={ShipTabs.Availability} />
								)}

								<Tab label={Language("DOCUMENTS")} value={ShipTabs.Documents} />

								{hasPluginPermission(session, "tonnageCertificates") && (
									<Tab label={Language("SHIP_TONNAGE_CERTIFICATES")} value={ShipTabs.TonnageCertificates} />
								)}

								{hasPluginPermission(session, "agents") && (
									<Tab label={Language("AGENCIES")} value={ShipTabs.Agents} />
								)}

								{ship.bargeType === BargeType.Tanker &&
									hasOptionalFieldPermission(session, Fields.SHIP, "tanks") && (
									<Tab label={Language("PREVIOUS_CARGOS")} value={ShipTabs.PreviousCargoes} />
								)}
							</Tabs>
						</Box>

						<Box p={[
							ShipTabs.Contacts,
							ShipTabs.Documents,
							ShipTabs.TonnageCertificates,
							ShipTabs.Agents,
							ShipTabs.PreviousCargoes,
						].includes(selectedTab) ? 0 : 2}>
							{selectedTab === ShipTabs.General && (
								<ShipGeneralDetails ship={ship} />
							)}

							{selectedTab === ShipTabs.Holds && (
								<ShipHoldDetails ship={ship} />
							)}

							{selectedTab === ShipTabs.Tanks && (
								<ShipTanksDetails
									tanks={ship.tanks}
									tankConfiguration={ship.tankProperties?.tankConfiguration ?? null}
									showCargos={false} />
							)}

							{/*TODO: Reimplement in the future*/}
							{/*{selectedTab === TABS.LAYER_CAPACITIES && (*/}
							{/*	<LayerCapacities ship={ship} />*/}
							{/*)}*/}

							{selectedTab === ShipTabs.Contacts && (
								<ShipContactDetails ship={ship} />
							)}

							{selectedTab === ShipTabs.Availability && (
								<ShipAvailabilityDetails ship={ship} refetch={refetch} />
							)}

							{selectedTab === ShipTabs.Documents && (
								<Documents
									entities={[ {
										id: ship.id,
										name: ship.name,
										type: DocumentEntity.Ship,
									} ]}
									showOptionalColumns={[
										DocumentOptionalColumns.DocumentType,
										DocumentOptionalColumns.IssuedAt,
										DocumentOptionalColumns.ValidUntil,
									]}
									documentTypeOptions={[
										DocumentType.Certificate,
										DocumentType.Map,
										DocumentType.TonnageCertificate,
										DocumentType.Insurance,
										DocumentType.Other,
									]} />
							)}

							{selectedTab === ShipTabs.TonnageCertificates && (
								<ShipTonnageCertificatesTable tonnageCertificates={ship.tonnageCertificates ?? []} />
							)}

							{selectedTab === ShipTabs.Agents && (
								<ShipAgentsTable agents={ship.agents ?? []} />
							)}

							{selectedTab === ShipTabs.PreviousCargoes && (
								<LastShippedProductsTable ship={ship} />
							)}
						</Box>
					</Card>
				</Grid>
			</Grid>

			<UpdateShipNameDialog
				open={dialogOpen}
				onClose={() => openDialog(false)}
				ship={ship} />
		</div>
	);
};

const Details = ({ ship }: { ship: ShipFromQuery }) => {
	const session = useStoreState(store => store.session);

	return (
		<React.Fragment>
			<FormGroup label={Language("SHIP_ADD_NAME")} labelPlacement="left-center" overflow="hidden">
				{ship.name ?? "-"}
			</FormGroup>

			<FormGroup label={Language("SHIP_ADD_VESSEL_TYPE")} labelPlacement="left-center" overflow="hidden">
				{intl.formatMessage({ id: ship.vesselType ?? "" })}
			</FormGroup>

			{hasOptionalFieldPermission(session, Fields.SHIP, "abbreviation") && (
				<FormGroup label={Language("SHIPS_ABBREVIATION")} labelPlacement="left-center" overflow="hidden">
					{ship.abbreviation ?? "-"}
				</FormGroup>
			)}

			{hasPluginPermission(session, "fleets") && (
				<FormGroup
					label={Language("FLEETS")}
					labelPlacement="left"
					labelPaddingTop={0}
					overflow="hidden">
					{ship.fleets.map((fleet, index) => (
						<div style={{ margin: 0 }} key={index}>
							<FleetLink fleet={fleet} />
						</div>
					))}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "isDummy") && (
				<FormGroup label={Language("SHIP_DUMMY")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(ship.isDummy)}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "externalId") && (
				<FormGroup label={Language("EXTERNAL_ID")} labelPlacement="left-center" overflow="hidden">
					{ship.externalId ?? "-"}
				</FormGroup>
			)}

			{hasImoPermission(session, ship) && (
				<FormGroup label={Language("SHIP_ADD_IMO")} labelPlacement="left-center" overflow="hidden">
					{ship.imo ?? "-"}
				</FormGroup>
			)}

			{hasEniPermission(session, ship) && (
				<FormGroup label={Language("SHIP_ADD_ENI")} labelPlacement="left-center" overflow="hidden">
					{ship.eni ?? "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "mmsi") && (
				<FormGroup label={Language("SHIPS_MMSI")} labelPlacement="left-center" overflow="hidden">
					{ship.mmsi ?? "-"}
				</FormGroup>
			)}

			{hasLicensePlatePermission(session, ship) && (
				<FormGroup label={Language("SHIPS_LICENSE_PLATE")} labelPlacement="left-center" overflow="hidden">
					{ship.licensePlate ?? "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "gmp") && (
				<FormGroup label={Language("SHIP_GMP")} labelPlacement="left-center" overflow="hidden">
					{ship.gmp ?? "-"}
				</FormGroup>
			)}

			{hasNiwoPermission(session, ship) && (
				<FormGroup label={Language("SHIP_NIWO")} labelPlacement="left-center" overflow="hidden">
					{ship.niwo ?? "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "cemtClassificationType") && (
				<FormGroup label={Language("SHIP_CEMT_CLASSIFICATION_TYPE")} labelPlacement="left-center" overflow="hidden">
					{ship.cemtClassificationType?.canonicalName ?? "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "yearOfBuild") && (
				<FormGroup label={Language("SHIPS_YEAR_OF_BUILD")} labelPlacement="left-center" overflow="hidden">
					{ship.yearOfBuild ?? "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "flag") && (
				<FormGroup label={Language("SHIP_FLAG")} labelPlacement="left-center" overflow="hidden">
					{ship.flag ?? "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "bargeType") && (
				<FormGroup label={Language("BARGE_TYPE")} labelPlacement="left-center" overflow="hidden">
					{ship.bargeType ? Language(ship.bargeType) : "-"}
				</FormGroup>
			)}

			{hasClassificationSocietyPermission(session, ship) && (
				<FormGroup label={Language("SHIP_CLASSIFICATION_SOCIETY")} labelPlacement="left-center" overflow="hidden">
					{ship.classificationSociety ?? "-"}
				</FormGroup>
			)}

			{hasInsurerPermission(session) && (
				<FormGroup label={Language("SHIPS_INSURER")} labelPlacement="left-center" overflow="hidden">
					{ship.insurer ?? "-"}
				</FormGroup>
			)}

			{hasAdnShipTypePermission(session, ship) && (
				<FormGroup label={Language("SHIP_ADN_SHIP_TYPE")} labelPlacement="left-center" overflow="hidden">
					{adnShipType(ship.adnProperties?.shipType)}
				</FormGroup>
			)}

			{hasAdnCargoTankDesignPermission(session, ship) && (
				<FormGroup label={Language("SHIP_ADN_CARGO_TANK_DESIGN")} labelPlacement="left-center" overflow="hidden">
					{adnCargoTankDesign(ship.adnProperties?.cargoTankDesign)}
				</FormGroup>
			)}

			{hasAdnCargoTankTypePermission(session, ship) && (
				<FormGroup label={Language("SHIP_ADN_CARGO_TANK_TYPE")} labelPlacement="left-center" overflow="hidden">
					{adnCargoTankType(ship.adnProperties?.cargoTankType)}
				</FormGroup>
			)}

			{hasTankCladdingTypePermission(session, ship) && (
				<FormGroup label={Language("SHIP_TANK_CLADDING_TYPE")} labelPlacement="left-center" overflow="hidden">
					{ship.tankCladdingType ? intl.formatMessage({ id: `SHIP_TANK_CLADDING_TYPE_${ship.tankCladdingType}` }) : "-"}
				</FormGroup>
			)}

			{ship.bargeType !== BargeType.PusherTug && hasOptionalFieldPermission(session, Fields.SHIP, "capacityTon") && (
				<FormGroup label={Language("SHIPS_CAPACITY_TON")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.tonnage(ship.capacityTon)}
				</FormGroup>
			)}

			{ship.bargeType !== BargeType.PusherTug && hasOptionalFieldPermission(session, Fields.SHIP, "capacityM3") && (
				<FormGroup label={Language("SHIPS_CAPACITY_M3")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.volumeM3(ship.capacityM3)}
				</FormGroup>
			)}

			{hasCapacityTeuPermission(session, ship) && (
				<FormGroup label={Language("CAPACITY_TEU")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.teu(ship.capacityTeu)}
				</FormGroup>
			)}

			{hasTracksPermission(session, ship) && (
				<FormGroup label={Language("TRACKS")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.meter(ship.tracks ? ship.tracks / 100 : undefined)}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "dimensions") && (
				<React.Fragment>
					<FormGroup label={Language("SHIPS_LENGTH")} labelPlacement="left-center" overflow="hidden">
						{DataFormat.meter(ship.lengthCm && ship.lengthCm / 100 )}
					</FormGroup>

					<FormGroup label={Language("SHIPS_WIDTH")} labelPlacement="left-center" overflow="hidden">
						{DataFormat.meter(ship.widthCm && ship.widthCm / 100)}
					</FormGroup>

					<FormGroup label={Language("SHIPS_MIN_DRAUGHT")} labelPlacement="left-center" overflow="hidden">
						{DataFormat.meter(ship.minDraughtCm && ship.minDraughtCm / 100)}
					</FormGroup>

					<FormGroup label={Language("SHIPS_MAX_DRAUGHT")} labelPlacement="left-center" overflow="hidden">
						{DataFormat.meter(ship.maxDraughtCm && ship.maxDraughtCm / 100)}
					</FormGroup>

					{hasPumpCapacityPermission(session, ship) && (
						<FormGroup label={Language("SHIP_PUMP_CAPACITY")} labelPlacement="left-center" overflow="hidden">
							{DataFormat.volumePerHourM3(ship.pumpCapacity)}
						</FormGroup>
					)}

					{hasNumberOfTanksPermission(session, ship) && (
						<FormGroup label={Language("SHIP_NUMBER_OF_TANKS")} labelPlacement="left-center" overflow="hidden">
							{ship.numberOfTanks}
						</FormGroup>
					)}
				</React.Fragment>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "fuelType") && (
				<FormGroup label={Language("SHIPS_FUEL_TYPE")} labelPlacement="left-center" overflow="hidden">
					{ship.fuelType ? Language(ship.fuelType) : "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "avgVelocityEmptyKmH") && (
				<FormGroup label={Language("SHIPS_AVG_VELOCITY_EMPTY")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.kmH(ship.avgVelocityEmptyKmH)}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "avgVelocityFullKmH") && (
				<FormGroup label={Language("SHIPS_AVG_VELOCITY_FULL")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.kmH(ship.avgVelocityFullKmH)}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "avgFuelConsumptionLtrH") && (
				<FormGroup label={Language("SHIPS_AVG_FUEL_CONSUMPTION_HOUR")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.ltrH(ship.avgFuelConsumptionLtrH)}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "avgFuelConsumptionLtrKm") && (
				<FormGroup label={Language("SHIPS_AVG_FUEL_CONSUMPTION_KM")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.ltrKm(ship.avgFuelConsumptionLtrKm)}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "yearLastMaintenance") && (
				<FormGroup label={Language("SHIPS_YEAR_LAST_MAINTENANCE")} labelPlacement="left-center" overflow="hidden">
					{ship.yearLastMaintenance ?? "-"}
				</FormGroup>
			)}

			{hasBobbyCatPermission(session, ship) && (
				<FormGroup label={Language("SHIPS_BOBBY_CAT")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(ship.bobbyCat)}
				</FormGroup>
			)}

			{hasSailersPermission(session, ship) && (
				<FormGroup label={Language("SHIPS_SAILORS")} labelPlacement="left-center" overflow="hidden">
					{ship.sailors ?? "-"}
				</FormGroup>
			)}

			{hasReeferConnectionPermission(session, ship) && (
				<FormGroup label={Language("SHIPS_NUM_REEFER_CONNECTIONS")} labelPlacement="left-center" overflow="hidden">
					{ship.numReeferConnections ?? "-"}
				</FormGroup>
			)}

			{hasDroplinePermission(session, ship) && (
				<FormGroup label={Language("SHIPS_DROPLINE")} labelPlacement="left-center" overflow="hidden">
					{ship.dropline ?? "-"}
				</FormGroup>
			)}

			{hasTrussingPermission(session, ship) && (
				<FormGroup label={Language("SHIP_TRUSSING")} labelPlacement="left-center" overflow="hidden">
					{ship.trussing ?? "-"}
				</FormGroup>
			)}

			{hasPushBowPermission(session, ship) && (
				<FormGroup label={Language("PUSH_BOW")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(ship.hasPushBow)}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "pushingCertificate") && (
				<FormGroup label={Language("PUSHING_CERTIFICATE")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(ship.hasPushingCertificate)}
				</FormGroup>
			)}

			{hasCellGuidesPermission(session, ship) && (
				<FormGroup label={Language("SHIP_CELL_GUIDES")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(ship.cellGuides)}
				</FormGroup>
			)}

			{hasShuttersPermission(session, ship) && (
				<FormGroup label={Language("SHIP_HAS_SHUTTERS")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(ship.hasShutters)}
				</FormGroup>
			)}

			{hasShuttersTypePermission(session, ship) && (
				<FormGroup label={Language("SHIP_SHUTTERS_TYPE")} labelPlacement="left-center" overflow="hidden">
					{ship.shuttersType ?? "-"}
				</FormGroup>
			)}

			{hasDeckLoadPermission(session, ship) && (
				<FormGroup label={Language("SHIP_DECK_LOAD")} labelPlacement="left-center" overflow="hidden">
					{ship.deckLoad ?? "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "avgSpeedUpstreamKmH") && (
				<FormGroup label={Language("SHIP_AVG_SPEED_UPSTREAM_KMH")} labelPlacement="left-center" overflow="visible">
					{DataFormat.kmH(ship.avgSpeedUpstreamKmH)}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "avgSpeedDownstreamKmH") && (
				<FormGroup label={Language("SHIP_AVG_SPEED_DOWNSTREAM_KMH")} labelPlacement="left-center" overflow="visible">
					{DataFormat.kmH(ship.avgSpeedDownstreamKmH)}
				</FormGroup>
			)}

			{hasNumberOfCranesPermission(session, ship) && (
				<FormGroup label={Language("SHIP_NUMBER_OF_CRANES")} labelPlacement="left-center" overflow="hidden">
					{ship.numberOfCranes ?? "-"}
				</FormGroup>
			)}

			{hasMaxCranesOnQuaysPermission(session, ship) && (
				<FormGroup label={Language("SHIP_MAX_CRANES_ON_QUAY")} labelPlacement="left-center" overflow="hidden">
					{ship.maxCranesOnQuay ?? "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "trackerId") && (
				<FormGroup label={Language("SHIPS_TRACKER_ID")} labelPlacement="left-center" overflow="hidden">
					{ship.trackerId ?? "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "trackTraceProvider") && (
				<FormGroup label={Language("SHIPS_TRACK_TRACE_PROVIDER")} labelPlacement="left-center" overflow="hidden">
					{ship.trackTraceProvider ?? "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "maxTonBallast") && (
				<FormGroup label={Language("SHIPS_MAX_TON_BALLAST")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.tonnage(ship.maxTonBallast)}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "airDraft") && (
				<FormGroup label={Language("SHIPS_AIR_DRAIFT")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.meter(ship.airDraft)}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "corridor") && ship.bargeType !== BargeType.Tanker && (
				<FormGroup label={Language("SHIPS_CORRIDOR")} labelPlacement="left-center" overflow="hidden">
					{ship.corridor ?? "-"}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "tankEye") && (
				<FormGroup label={Language("TANK_EYE")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(ship.tankEye)}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "hasHeatingInstallation") && (
				<FormGroup label={Language("HAS_HEATING_INSTALLATION")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(ship.hasHeatingInstallation)}
				</FormGroup>
			)}

			{hasBoilerPermission(session, ship) && (
				<FormGroup label={Language("SHIP_BOILER")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(ship.boiler)}
				</FormGroup>
			)}

			{hasSpillRailPermission(session, ship) && (
				<FormGroup label={Language("SHIP_SPILL_RAIL")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(ship.spillRail)}
				</FormGroup>
			)}

			{ship.bargeType === BargeType.PushedBarge && (
				<FormGroup label={Language("SELF_PROPELLED")} labelPlacement="left-center" overflow="hidden">
					{DataFormat.boolean(ship.selfPropelled)}
				</FormGroup>
			)}

			{hasOptionalFieldPermission(session, Fields.SHIP, "sailingSchedule") && (
				<FormGroup label={Language("VOYAGE_SCHEDULE")} labelPlacement="left-center" overflow="hidden">
					{ship.sailingSchedule?.schedule ?? "-"}
				</FormGroup>
			)}

			{hasMemoPermission(session) && (
				<FormGroup label={Language("SHIPS_MEMO")} labelPlacement="left-center" overflow="hidden">
					<span style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
						{ship.memo ?? "-"}
					</span>
				</FormGroup>
			)}

			{hasMemoPermission(session) && (
				<FormGroup label={Language("SHIP_PLANNING_NOTES")} labelPlacement="left-center" overflow="hidden">
					<span style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
						{ship.planningNotes ?? "-"}
					</span>
				</FormGroup>
			)}
		</React.Fragment>
	);
};

export default ShipDetails;
